import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import {
  signalRService,
  type SignalRNotification,
} from "Infrastructure/SignalR/SignalRService";
import { notificationsKeys } from "Api/Queries/Notifications/useNotifications";
import { newNotificationsCountKeys } from "Api/Queries/Notifications/useNewNotificationsCount";
import type { PagedNotificationResult } from "Infrastructure/Api/Api";
import { logError } from "Infrastructure/Utils/LoggingUtils";
import { isAuthenticated, JWT_KEY } from "Utils/AuthUtils";
import { getLocalStorageItem } from "Utils/Storage/LocalStorage/SecureLocalStorage";
import type { RootStateType } from "State/Store";

export const useSignalRNotifications = () => {
  const queryClient = useQueryClient();
  const user = useSelector((state: RootStateType) => state.auth.user);
  const isUserAuthenticated = isAuthenticated(user);

  useEffect(() => {
    let isConnectionInitialized = false;

    const handleReceiveNotification = (notification: SignalRNotification) => {
      queryClient.setQueriesData<PagedNotificationResult>(
        { queryKey: notificationsKeys.all },
        (old) => {
          if (!old) {
            return old;
          }

          const exists = old.items.some(
            (n) => n.notificationPublicID === notification.notificationPublicID,
          );

          if (exists) {
            return old;
          }

          return {
            ...old,
            items: [notification, ...old.items],
            totalCount: old.totalCount + 1,
          };
        },
      );

      queryClient.setQueryData<{ count: number }>(
        newNotificationsCountKeys.all,
        (old) => ({
          count: (old?.count || 0) + 1,
        }),
      );
    };

    const initializeConnection = async () => {
      const token = getLocalStorageItem(JWT_KEY);
      const shouldSkipInitialization = !isUserAuthenticated || !token;

      if (shouldSkipInitialization) {
        return;
      }

      try {
        await signalRService.startConnection();
        signalRService.onReceiveNotification(handleReceiveNotification);
        isConnectionInitialized = true;
      } catch (error) {
        logError(
          error as Error,
          { message: "Failed to initialize SignalR connection" },
          false,
        );
      }
    };

    initializeConnection();

    return () => {
      // Only cleanup if we successfully initialized the connection
      if (isConnectionInitialized) {
        signalRService.offReceiveNotification(handleReceiveNotification);
        signalRService.stopConnection();
      }
    };
  }, [queryClient, isUserAuthenticated]);
};
